import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, CircleCheck, Copy, TriangleAlert } from "lucide-react";
import { fetchApi } from "../../components/marketing/access";

/**
 * Settings → Integrations → Meta Conversions API → guide (no design screen; built
 * like IntegrationDetail). Same content and call as the old
 * pages/settings/Integrations/MetaConversionsGuide.js.
 *
 * Three of the setup steps happen inside Meta's own tools, and skipping any of
 * them leaves a setup that looks finished but silently does nothing: Meta accepts
 * events into an unregistered dataset and returns success. So this page spells out
 * each step, says what to verify, and pulls in the org's own page and ad account
 * (GET /api/integrations/meta-conversions/context -> { org_name, page_name, page_id,
 * ad_account_id }) so nobody has to guess which Business Manager to use. The guide
 * reads fine without them; a failed load only says so in one line.
 */

const SETUP = "/settings/integrations/meta-conversions";
const linkCls = "font-semibold text-sky-deep underline";
const code = "rounded-xs bg-mist px-1.5 py-0.5 font-mono text-caption";

function Pill({ children, tone }) {
  return <span className={`rounded-full px-2 py-0.5 text-caption font-medium ${tone === "warning" ? "bg-warning-soft text-warning" : "bg-mist text-muted"}`}>{children}</span>;
}

function Callout({ tone, children }) {
  const cls = tone === "success" ? "bg-success-soft text-success" : tone === "warning" ? "bg-warning-soft text-warning" : "bg-mist text-ink/80";
  return <div className={`rounded-md p-3 text-caption ${cls}`}>{children}</div>;
}

function Copyable({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => { void navigator.clipboard?.writeText(String(value)); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2 last:border-0">
      <span className="shrink-0 text-caption text-muted">{label}</span>
      <button type="button" onClick={copy} title="Copy" className="flex min-w-0 items-center gap-1.5 font-mono text-caption hover:text-sky-deep">
        <span className="truncate">{value}</span>
        {copied ? <Check size={13} strokeWidth={2} className="shrink-0 text-success" /> : <Copy size={13} strokeWidth={1.75} className="shrink-0 text-muted" />}
      </button>
    </div>
  );
}

function Step({ n, title, time, warn, children }) {
  return (
    <li className="group relative pb-8 pl-12 last:pb-0">
      <span aria-hidden className="absolute top-9 bottom-0 left-[15px] w-px bg-line group-last:hidden" />
      <span className="absolute top-0 left-0 flex h-8 w-8 items-center justify-center rounded-full bg-sun text-small font-semibold">{n}</span>
      <div className="mb-2 flex flex-wrap items-center gap-2 pt-1">
        <h3 className="text-body font-semibold">{title}</h3>
        {time && <Pill>{time}</Pill>}
        {warn && <Pill tone="warning">Easy to miss</Pill>}
      </div>
      <div className="flex flex-col gap-3 text-small text-ink/80">{children}</div>
    </li>
  );
}

export default function MetaConversionsGuide() {
  const [org, setOrg] = useState(null);
  const [orgFailed, setOrgFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchApi("/api/integrations/meta-conversions/context").then((r) => {
      if (!live) return;
      if (r.ok) setOrg(r.data?.data || null);
      else setOrgFailed(true);
    });
    return () => { live = false; };
  }, []);

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-5 px-4 py-8 pb-12 sm:px-6 md:px-10">
      <Link to={SETUP} className="flex w-fit items-center gap-2 text-small font-semibold text-sky-deep"><ArrowLeft size={16} />Back to Meta Conversions API</Link>
      <header>
        <h1 className="text-h2 font-semibold">Connect Meta Conversions API</h1>
        <p className="mt-1 text-small text-muted">Tell Meta which of your leads turned into real customers, so your ads chase quality instead of volume.</p>
      </header>

      <section className="rounded-lg bg-paper p-5 shadow-soft">
        <h2 className="text-body font-semibold">What this actually does</h2>
        <p className="mt-2 text-small text-ink/80">Right now Meta only knows a form was submitted. It can't tell a serious enquiry apart from someone who typed a fake number, so it keeps finding you more of both.</p>
        <p className="mt-2 text-small text-ink/80">
          Once this is connected, every time you move a lead in Olli (Contacted, Qualified, Won) that update goes back to Meta. Meta learns which ads produce leads that actually convert, and shifts your budget towards them. Meta's own testing puts this at roughly <strong className="text-ink">19% lower cost per quality lead</strong>.
        </p>
      </section>

      <section className="flex items-start gap-3 rounded-lg bg-warning-soft p-5">
        <TriangleAlert size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-warning" />
        <div>
          <h2 className="text-small font-semibold">Read this before you start</h2>
          <p className="mt-1 text-small text-ink/80">Three of the steps happen in Meta's own tools, not here. If any of them is skipped, Meta will still <em>accept</em> the events and report success, but your campaigns won't be able to learn from them. Everything will look connected while doing nothing.</p>
          <p className="mt-2 text-small text-ink/80">Each step below tells you exactly what to look for to confirm it worked. Please don't skip those checks.</p>
        </div>
      </section>

      {org && (org.page_name || org.ad_account_id) && (
        <section className="rounded-lg bg-paper p-5 shadow-soft">
          <h2 className="text-body font-semibold">Your details</h2>
          <p className="mt-1 mb-3 text-caption text-muted">You'll need these below. Click any value to copy it.</p>
          <Copyable label="Facebook Page" value={org.page_name} />
          <Copyable label="Page ID" value={org.page_id} />
          <Copyable label="Ad account ID" value={org.ad_account_id} />
          {org.ad_account_id && (
            <p className="mt-3 text-caption text-warning">
              In Meta, make sure the Business Manager selected at the top left is the one that owns ad account <strong>{org.ad_account_id}</strong>. Building the dataset in the wrong business is the single most common reason this doesn't work.
            </p>
          )}
        </section>
      )}
      {orgFailed && <p className="text-caption text-muted">Your page and ad account details couldn't be loaded, so they aren't filled in below. The steps still apply.</p>}

      <section className="rounded-lg bg-paper p-5 shadow-soft sm:p-6">
        <ol>
          <Step n="1" title="Open Events Manager" time="1 min">
            <p>
              Go to <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className={`${linkCls} break-all`}>business.facebook.com/events_manager2</a>, or from Meta Business Suite open <strong>All tools</strong> in the left sidebar and pick <strong>Events Manager</strong> under "Advertise".
            </p>
            <Callout><strong className="text-ink">Check the business first.</strong> Top left shows which Business Manager you're in. It must be the one that runs your Facebook ads.</Callout>
          </Step>

          <Step n="2" title="Create a CRM dataset" time="3 min" warn>
            <p>Click <strong>Connect data sources</strong> (green button, top left). Choose <strong>CRM</strong>, then <strong>Next</strong>.</p>
            <p>When asked, pick <strong>Create a new dataset</strong> and give it a clear name like <span className={code}>{org?.org_name ? `${org.org_name} — CRM` : "My Clinic — CRM"}</span>.</p>
            <Callout><strong className="text-ink">Make a new one, don't reuse your website dataset.</strong> Meta recommends keeping CRM events separate from website traffic, otherwise the two data types get mixed and neither reports cleanly.</Callout>
            <p>
              It will ask which CRM you use. Olli won't be in the list, so choose the option that means "set it up myself" (worded as <strong>I'll manage the API myself</strong>, <strong>Set up manually</strong>, or <strong>Use the Conversions API directly</strong>). Do <em>not</em> pick a partner integration.
            </p>
            <Callout tone="success"><span className="flex items-start gap-1.5"><CircleCheck size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" /><span><strong>Confirm it worked:</strong> your new dataset should now show a <strong>CRM</strong> badge in the data sources list. No badge means the connection didn't complete; repeat this step.</span></span></Callout>
          </Step>

          <Step n="3" title="Turn the integration on" time="1 min" warn>
            <p>Open the dataset you just made and find its <strong>Settings</strong> or <strong>Overview</strong> tab. Look for <strong>Enable Integration</strong> (sometimes shown as a "Conversion leads" toggle) and switch it on.</p>
            <Callout tone="warning"><strong>This is the step people miss.</strong> Without it Meta accepts your events but campaigns cannot optimise against them. You'd see events arriving and no improvement, with nothing obviously broken.</Callout>
          </Step>

          <Step n="4" title="Copy the Dataset ID and access token" time="2 min">
            <p><strong>Dataset ID</strong>: the 15-16 digit number shown under the dataset name.</p>
            <p><strong>Access token</strong>: in the dataset's <strong>Settings</strong> tab, scroll to the <strong>Conversions API</strong> section and click <strong>Generate access token</strong>.</p>
            <Callout tone="warning">Meta shows this token <strong>once</strong>. Copy it straight into Olli in the next step, or somewhere safe first. Treat it like a password.</Callout>
          </Step>

          <Step n="5" title="Paste them into Olli" time="1 min">
            <p>Head back to <Link to={SETUP} className={linkCls}>the Meta Conversions API settings</Link> and fill in your access token and Dataset ID, then save.</p>
          </Step>

          <Step n="6" title="Send a test event" time="2 min">
            <p>In Events Manager, open your dataset and go to the <strong>Test Events</strong> tab. It shows a code like <span className={code}>TEST12345</span>.</p>
            <p>Paste that code into the <strong>Send a test event</strong> box on the settings page and hit Send. Then watch the Test Events tab; your event should appear within a few seconds.</p>
            <Callout tone="success"><span className="flex items-start gap-1.5"><CircleCheck size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" /><span>Event shows up = the whole chain works. The test uses one of your real leads so the lead matching gets checked too, and the code keeps it out of your actual reporting.</span></span></Callout>
          </Step>

          <Step n="7" title="Confirm your custom events" time="2 min" warn>
            <p>Once events start arriving, Events Manager shows a red banner: <em>"Confirm custom event(s) that belong to you."</em> Click <strong>Review events</strong>, tick your stage names, and accept.</p>
            <Callout tone="warning">Skip this and everything still looks healthy (events arrive, the dashboard says "firing properly") but Meta will not let your campaign optimise on them. It is the quietest way for this setup to fail.</Callout>
            <p>It is a <strong>one-time click per event name</strong>, not per lead. <strong>Lead</strong> and <strong>Purchase</strong> are Meta standard events and never need it; your other stage names (Contacted, Qualified, Proposal, Negotiation, Lost, Junk) are custom and do.</p>
            <p className="text-caption text-muted">Meta only lists an event after it has received it at least once, so stages no lead has reached yet will not appear. Expect the banner to come back the first time a lead lands on a new stage, including any status you add later. Confirm it once and it stays confirmed.</p>
          </Step>

          <Step n="8" title="Switch your campaign to Conversion Leads" time="3 min" warn>
            <p>In <strong>Ads Manager</strong>, open your lead campaign and set the performance goal to <strong>Maximise number of conversion leads</strong>.</p>
            <Callout tone="warning">Do this on a <strong>new or duplicated campaign</strong> where you can. Changing the goal on a running campaign restarts its learning phase and performance dips for a while.</Callout>
            <p className="text-caption text-muted">Meta needs roughly 50 conversion events before it has enough signal to optimise, so give it a few weeks.</p>
          </Step>

          <Step n="9" title="Sort your stages into a funnel" time="3 min">
            <p>After about <strong>7 days</strong> of events, Events Manager unlocks <strong>Configure sales funnel</strong> on your dataset's Overview tab. Drag the stages that mean progress (Contacted, Qualified, Proposal, Negotiation, Won) into order under <strong>Positive stages</strong>, and leave <strong>Lost</strong> and <strong>Junk</strong> under <strong>Other stages</strong>.</p>
            <p>This is why Olli sends the losing stages too. Telling Meta which leads went nowhere is often worth more than telling it which ones closed; junk is usually where the budget leaks.</p>
            <p className="text-caption text-muted">Pick a stage to optimise for that roughly 1-40% of leads reach, and that they reach within 28 days. Too rare and Meta never gathers enough signal; too common and it is not telling delivery anything new. If your campaign already has a stage selected in Ads Manager, this screen stays locked; change it there instead.</p>
          </Step>
        </ol>
      </section>

      <section className="rounded-lg bg-paper p-5 shadow-soft">
        <h2 className="text-body font-semibold">Once it's running</h2>
        <p className="mt-2 text-small text-ink/80">Every lead status change sends an event automatically. You can watch them on the <Link to={SETUP} className={linkCls}>settings page</Link> under <strong>Lead status events</strong>; it shows what was sent, what was skipped, and why.</p>
        <p className="mt-2 text-small text-ink/80">
          Which statuses send events is up to you. Open <Link to="/settings/lead-statuses" className={linkCls}>Settings → Lead statuses</Link> and use the "Tell Meta when a lead reaches this stage" checkbox on any stage. Every stage sends by default, losing ones included; that is what Meta's funnel expects. Any status you create gets an event name derived from its label automatically, so remember step 7 for it.
        </p>
      </section>

      <section className="rounded-lg bg-paper p-5 shadow-soft">
        <h2 className="text-body font-semibold">If something isn't working</h2>
        <div className="mt-3 flex flex-col gap-3">
          {[
            { q: "Events show as \"Skipped\"", a: "The reason is printed next to each one. \"Conversions API not configured\" means steps 4-5 aren't done. \"Stage has no Meta event name\" means that status is deliberately set not to send; check Settings → Lead statuses." },
            { q: "Test event never arrives in Events Manager", a: "Usually the Dataset ID belongs to a different Business Manager than the one you are viewing, or the token was generated on another dataset. Re-copy both from the same dataset page." },
            { q: "Events arrive but campaigns don't improve", a: "Events landing is not the same as campaigns being able to use them. Check step 7 (confirm your custom events) first: unconfirmed custom events arrive but cannot be optimised on, and nothing warns you. Then step 3 (Enable Integration) and step 8 (Conversion Leads goal)." },
            { q: "Nothing changed after a few days", a: "Meta needs around 50 conversion events to learn. If you get a handful of qualified leads a week, expect a few weeks before delivery shifts." },
          ].map((item) => (
            <div key={item.q} className="rounded-md bg-mist p-3">
              <p className="text-small font-medium">{item.q}</p>
              <p className="mt-1 text-caption text-muted">{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
